import classnames from "classnames";
import React, { useEffect } from "react";
import AdsenseDisplay from "web/react/components/adsense-display/adsense-display";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import BrandAdContainer from "web/react/components/brand-ad-container/brand-ad-container";
import Breadcrumbs from "web/react/components/breadcrumbs/breadcrumbs";
import BuyArea from "web/react/components/buybuybuy-area/buy-area/buy-area";
import Conditional from "web/react/components/conditional/conditional";
// Components
import CustomerCaptureOverlay from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import ProductsCarousel from "web/react/components/products-carousel/products-carousel";
import RecentlyViewed from "web/react/components/recently-viewed/recently-viewed";
import RelatedDesignersRail from "web/react/components/related-designers/related-designers-rail";
import RelatedProductsArea from "web/react/components/related-products-area/related-products-area";
import RelatedProductsSidebar from "web/react/components/related-products-sidebar/related-products-sidebar";
import RelatedSearchesRail from "web/react/components/related-searches-rail/related-searches-rail";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
// Styles
import layout from "web/react/pages/product/in-stock/in-stock-layout.css";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { InStockIconProductLayoutSerializer } from "web/types/serializers";
import styles from "./in-stock-icon.css";

interface InStockICONProductPageProps {
    productLayout: InStockIconProductLayoutSerializer;
    csfr: string;
    helpCentreURLs: string[];
    showCookieSettingsOption: boolean;
    country: string;
}

function InStockICONProductPage({
    productLayout,
    csfr,
    helpCentreURLs,
    showCookieSettingsOption,
    country,
}: InStockICONProductPageProps): React.ReactElement {
    const {
        customer_capture_data: customerCaptureData,
        product_analytics_data: productAnalyticsData,
        product_overview: productOverview,
        related_products_sidebar: relatedProductsSidebar,
        product_edit_button: productEditButton,
        adsense_for_shopping: adsenseForShopping,
        adsense_for_display: adsenseForDisplay,
        brand_ad: brandAd,
        related_products: relatedProducts,
        related_designers_type_rail: relatedDesignersTypeRail,
        related_designers_category_rail: relatedDesignersCategoryRail,
        related_designers_subcategory_rail: relatedDesignersSubcategoryRail,
        related_search_rail: relatedSearchRail,
        product_breadcrumbs: productBreadcrumbs,
        recently_viewed: recentlyViewedProducts,
        curated_ads: curatedAds,
        footer,
        features,
    } = productLayout;

    useEffect(() => {
        analytics.tagEvent(analytics.TAG_NETWORKS.FACEBOOK, facebookEvents.VIEW_CONTENT, {
            content_ids: [(productAnalyticsData as any).product_id],
            content_type: "product",
        });

        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    return (
        <div className={layout.container}>
            <CustomerCaptureOverlay {...customerCaptureData} />

            <div className={layout.primary}>
                <article
                    className={classnames(layout.info, {
                        [layout.infoWithSidebar]: !!relatedProductsSidebar,
                    })}
                >
                    <div className={layout.gallery}>
                        <Conditional
                            check={productEditButton !== null && productEditButton.is_user_staff}
                            key="edit-button"
                        >
                            <a
                                href={`https://os.lystit.com/product-data/product/${productEditButton.product_slug}/${productEditButton.product_id}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={layout.edit}
                            >
                                <button className="button black">
                                    {gettext("product_edit_button.label")}
                                </button>
                            </a>
                        </Conditional>

                        <PDPGallery
                            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                            product={productOverview!.product_image_gallery}
                            hasRelatedProductsSidebar={!!relatedProductsSidebar}
                        />
                    </div>

                    <div className={layout.details}>
                        {productOverview?.save_for_later &&
                            productOverview.buybuybuy_area?.product_id && (
                                <SaveForLaterButton
                                    cta={
                                        productOverview.save_for_later.save_for_later_signup_prompt
                                            ?.cta_text
                                    }
                                    pageSource="pdp"
                                    productId={productOverview.buybuybuy_area?.product_id.toString()}
                                    isLoggedIn={userProfiler.isLoggedIn()}
                                    type="small"
                                    className={styles.saveForLater}
                                />
                            )}
                        {productOverview?.buybuybuy_area && (
                            <BuyArea
                                header={productOverview.product_header}
                                buyAreaOptions={productOverview.buybuybuy_area}
                                productDescription={productOverview.product_description}
                                productMoreDescriptions={productOverview.product_more_descriptions}
                                csrfToken={csfr}
                            />
                        )}
                        {productOverview?.related_products && (
                            <div>
                                <div>
                                    <RelatedProductsSidebar
                                        relatedProductsSidebar={productOverview.related_products}
                                        variant="horizontal"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </article>

                {relatedProductsSidebar && (
                    <RelatedProductsSidebar
                        relatedProductsSidebar={relatedProductsSidebar}
                        variant="vertical"
                        className={classnames(layout.sidebar, "affiliateSidebar")}
                    />
                )}
            </div>

            <div className={layout.secondary}>
                {curatedAds && (
                    <ProductsCarousel title={curatedAds.title} products={curatedAds.products} />
                )}
                {!curatedAds && adsenseForShopping && <AdsenseShopping {...adsenseForShopping} />}

                {!curatedAds && brandAd && <BrandAdContainer {...brandAd} />}

                {relatedProducts && (
                    <RelatedProductsArea
                        sections={relatedProducts.sections}
                        hideShortDescription={environment.getFeature(
                            "seo_hide_desc_related_is_pdp"
                        )}
                        isAffiliate={false}
                    />
                )}

                {adsenseForDisplay && (
                    <AdsenseDisplay
                        divId={adsenseForDisplay.div_id}
                        slotId={adsenseForDisplay.slot_id}
                        usesMobileSize={adsenseForDisplay.uses_mobile_size}
                    />
                )}

                <div className={styles.brandAdContainer}>
                    {curatedAds && brandAd && <BrandAdContainer {...brandAd} />}
                </div>

                {curatedAds && adsenseForShopping && <AdsenseShopping {...adsenseForShopping} />}
            </div>

            {relatedDesignersTypeRail && !!relatedDesignersTypeRail.designers.length && (
                <RelatedDesignersRail
                    title={relatedDesignersTypeRail.title}
                    designers={relatedDesignersTypeRail.designers}
                    features={features}
                />
            )}

            {relatedDesignersCategoryRail && !!relatedDesignersCategoryRail.designers.length && (
                <RelatedDesignersRail
                    title={relatedDesignersCategoryRail.title}
                    designers={relatedDesignersCategoryRail.designers}
                    features={features}
                />
            )}

            {relatedDesignersSubcategoryRail &&
                !!relatedDesignersSubcategoryRail.designers.length && (
                    <RelatedDesignersRail
                        title={relatedDesignersSubcategoryRail.title}
                        designers={relatedDesignersSubcategoryRail.designers}
                        features={features}
                    />
                )}

            {relatedSearchRail && !!relatedSearchRail.related_searches.length && (
                <RelatedSearchesRail
                    title={gettext("product.related_products.related_searches")}
                    relatedSearches={relatedSearchRail.related_searches}
                    features={features as any}
                />
            )}

            {productBreadcrumbs && (
                <div className={layout.secondary}>
                    <Breadcrumbs breadcrumbs={productBreadcrumbs.breadcrumbs} type="pdp" />
                </div>
            )}

            <div className={layout.secondary}>
                {recentlyViewedProducts && (
                    <RecentlyViewed
                        title={recentlyViewedProducts.title}
                        products={recentlyViewedProducts.products}
                        is_loading={recentlyViewedProducts.is_loading}
                        current_product_id={recentlyViewedProducts.current_product_id}
                        in_overlay={recentlyViewedProducts.in_overlay}
                    />
                )}
            </div>

            <AppFooter
                data={footer}
                helpCenterUrls={helpCentreURLs}
                showCookieSettingsOption={showCookieSettingsOption}
                country={country}
            />
        </div>
    );
}

export default InStockICONProductPage;
